import { memo, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShieldIcon, UsersIcon } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { resolveBreadcrumbs } from '../lib/breadcrumbTrail';
import { useBreadcrumbContributions } from '../contexts/breadcrumbContextValues';
import { getReviewSource } from '../lib/reviewSource';
import { clampPopoverOffset } from '../lib/popoverPosition';
import { Breadcrumbs } from './Breadcrumbs';
import './Header.css';

interface HeaderProps {
  caseName?: string;
  // documentCount is preserved as a prop for back-compat with callers that
  // still pass it, but it is no longer rendered. Page-level titles and the
  // URL provide more accurate context than a global doc count, and the
  // prior centered "Stiiizy — 118,247 documents" framing baked the
  // direction-based aggregate into every screen. Hung's restructure moves
  // counts to per-module surfaces.
  documentCount?: number;
  // Optional save status next to breadcrumbs; hidden wrapper when empty.
  statusSlot?: ReactNode;
}

function caseKeyFromPath(pathname: string): string | null {
  // Case-insensitive, matching how React Router resolves the `/cases/:caseKey`
  // route; a literal-only test hid the Team menu and the trail on valid spellings.
  // The segment stays in its URL spelling — it is fed straight back into crumb
  // hrefs, and decoding it here would emit a malformed path.
  const segments = pathname.split('/');
  if (segments.length < 3) return null;
  let word = segments[1];
  try {
    word = decodeURIComponent(word);
  } catch {
    // keep the raw spelling
  }
  return word.toLowerCase() === 'cases' && segments[2] ? segments[2] : null;
}

export const Header = memo(function Header({ caseName, statusSlot }: HeaderProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Team dropdown — case-scoped only. Permissions is the sole entry today
  // (Case Issues was promoted out of the old Admin junk-drawer into a
  // top-level tab on the case workspace).
  const caseKey = caseKeyFromPath(location.pathname);
  const casePath = caseKey ? `/cases/${caseKey}` : undefined;
  const contributions = useBreadcrumbContributions();
  const crumbs = resolveBreadcrumbs({
    pathname: location.pathname,
    search: location.search,
    caseKey,
    caseName: caseName ?? null,
    reviewSource: getReviewSource(),
    contributions,
  });
  const showTeam = caseKey !== null;

  const handleLogout = useCallback(async () => {
    await signOut();
    navigate('/login', { replace: true });
  }, [signOut, navigate]);

  return (
    <header className="app-header">
      <div className="app-header__brand">
        <Link to="/cases" aria-label="Go to Cases"><Logo size="md" /></Link>
      </div>
      <div className="app-header__context">
        <Breadcrumbs crumbs={crumbs} casePath={casePath} />
        <div className="app-header__status">{statusSlot}</div>
      </div>
      <div className="app-header__actions">
        {showTeam && caseKey && (
          <TeamMenu onPermissions={() => navigate(`/cases/${caseKey}/members`)} />
        )}
        <button
          onClick={handleLogout}
          className="app-header__action text-sm text-gray-400 hover:text-white transition-colors"
        >
          Logout
        </button>
      </div>
    </header>
  );
});

function TeamMenu({ onPermissions }: { onPermissions: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelOffset, setPanelOffset] = useState(0);
  const panelId = useId();

  // The panel is right-aligned to a trigger that sits left of Logout, so on a
  // 320px viewport it used to overhang the left edge and clip the Permissions
  // row (#1476). Measure once per open and keep the panel inside the viewport
  // without moving it on desktop, where the clamp is a no-op.
  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel || !trigger) return;
    const place = () => {
      setPanelOffset(clampPopoverOffset({
        triggerRight: trigger.getBoundingClientRect().right,
        panelWidth: panel.offsetWidth,
        viewportWidth: window.innerWidth,
      }));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref} onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        // Keep native button clicks from becoming discovery review commands.
        event.stopPropagation();
        return;
      }
      if (!open || event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    }}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        className={`app-header__action flex items-center gap-1.5 text-sm transition-colors ${
          open ? 'text-white' : 'text-gray-400 hover:text-white'
        }`}
      >
        <UsersIcon className="w-4 h-4" />
        <span>Team</span>
      </button>
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          style={{ right: -panelOffset }}
          className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-navy-light bg-navy-light shadow-2xl shadow-black/40 overflow-hidden z-30"
        >
          <div className="py-1.5">
            <button
              onClick={() => {
                setOpen(false);
                onPermissions();
              }}
              className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-navy-lighter transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 bg-blue-500/10 text-blue-400">
                <ShieldIcon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-200">Permissions</div>
                <div className="text-xs text-gray-400 leading-snug mt-0.5">
                  Manage who has access to this case and their role.
                </div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
